import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CardImage } from "@/components/CardImage";
import { GradeBadge } from "@/components/GradeTooltip";
import { FaqSection } from "@/components/PageFaq";
import { ClusterLinks } from "@/components/ClusterLinks";
import { RARITY_GLOW } from "@/lib/game-constants";
import { useSession } from "@/lib/user-data";
import {
  acceptTradeOffer,
  getMyTradeOffers,
  getMyTradeStock,
  getTradeMarket,
  respondTradeOffer,
  sendCardTrade,
} from "@/lib/trades.functions";
import type { TradeMarketCard, TradeOfferRow } from "@/lib/trades-shared";
import { supabase } from "@/integrations/supabase/client";

const FAQ = [
  {
    q: "How do I trade Palworld cards with another player on Pal Stars?",
    a: "Flag a card as trade stock in your binder so it appears on the trade board, pick a card another member listed, choose one of your own cards to give and send the offer. When they accept, the two cards swap accounts instantly.",
  },
  {
    q: "Do I lose my card when I list it for trade?",
    a: "No. Listing a card only makes it visible on the Pal Stars trade board. Ownership only changes when you accept an offer on it, and you can pull it out of trade stock in the binder at any time.",
  },
  {
    q: "What happens to a traded card's grade and binder pocket?",
    a: "The PalStars Grade, sub-grades and noted defects travel with the card. The binder pocket does not: a traded card lands in the new owner's collection as an unfiled card so they can place it themselves.",
  },
  {
    q: "Can I cancel a Palworld card trade offer?",
    a: "Yes. The sender can cancel any pending offer and the recipient can decline it. Accepting one offer automatically cancels other pending offers on the same two cards.",
  },
  {
    q: "Is card trading free on Pal Stars?",
    a: "Yes. Trading is free for every Pal Stars account, alongside the 12 free daily booster packs, Palsino spins and the 108-pocket collector binder.",
  },
];

export const Route = createFileRoute("/trades")({
  head: () => ({
    meta: [
      { title: "Palworld Card Trading — Swap TCG Cards | Pal Stars" },
      { name: "description", content: "Trade Palworld Official Card Game cards with other collectors: list trade stock, pick a card, offer one of yours and swap accounts instantly." },
      { property: "og:title", content: "Palworld Card Trading on Pal Stars" },
      { property: "og:description", content: "List trade stock, choose a card another collector owns and swap it for one of yours — free for every Pal Stars account." },
      { property: "og:url", content: "https://palstars.com/trades" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/trades" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Pal Stars", item: "https://palstars.com/" },
                { "@type": "ListItem", position: 2, name: "Card Trading", item: "https://palstars.com/trades" },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: TradesPage,
});

interface StockRow {
  id: string;
  card_code: string;
  card_name: string;
  rarity: string;
  image: string;
  grade: number;
  grade_label: string;
  score: number;
  status: string;
}

function TradesPage() {
  const { session, loading } = useSession();
  const signedIn = !!session;

  const marketFn = useServerFn(getTradeMarket);
  const stockFn = useServerFn(getMyTradeStock);
  const offersFn = useServerFn(getMyTradeOffers);
  const sendFn = useServerFn(sendCardTrade);
  const acceptFn = useServerFn(acceptTradeOffer);
  const respondFn = useServerFn(respondTradeOffer);

  const [market, setMarket] = useState<TradeMarketCard[]>([]);
  const [stock, setStock] = useState<StockRow[]>([]);
  const [offers, setOffers] = useState<TradeOfferRow[]>([]);
  const [want, setWant] = useState<TradeMarketCard | null>(null);
  const [give, setGive] = useState<string>("");
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  /** Two-step offer flow: pick the cards, then confirm the swap. */
  const [step, setStep] = useState<"compose" | "confirm">("compose");
  /** Incoming offer awaiting an "are you sure?" before the cards swap. */
  const [confirmAccept, setConfirmAccept] = useState<TradeOfferRow | null>(null);
  /** Live trade status notifications (newest first). */
  const [alerts, setAlerts] = useState<{ id: string; text: string }[]>([]);

  const refresh = useCallback(async () => {
    if (!signedIn) return;
    try {
      const [m, s, o] = await Promise.all([marketFn({}), stockFn({}), offersFn({})]);
      setMarket(m as TradeMarketCard[]);
      setStock(s as StockRow[]);
      setOffers(o as TradeOfferRow[]);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not load the trade board.");
    }
  }, [signedIn, marketFn, stockFn, offersFn]);

  useEffect(() => { void refresh(); }, [refresh]);

  const userId = session?.user?.id;
  // Live trade status notifications: a new incoming offer, or an answer to one
  // of yours, pushes a banner and reloads the board.
  useEffect(() => {
    if (!userId) return;
    const push = (text: string) =>
      setAlerts((prev) => [{ id: `${Date.now()}-${Math.random()}`, text }, ...prev].slice(0, 4));
    const channel = supabase
      .channel(`trade-offers-${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "trade_offers", filter: `to_user=eq.${userId}` },
        () => { push("New trade offer received — see Pending offers below."); void refresh(); },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "trade_offers", filter: `from_user=eq.${userId}` },
        (payload) => {
          const status = (payload.new as { status?: string }).status;
          if (status && status !== "pending") {
            push(
              status === "accepted"
                ? "Your trade offer was accepted — the card is in your collection."
                : `Your trade offer was ${status}.`,
            );
            void refresh();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "trade_offers", filter: `to_user=eq.${userId}` },
        () => { void refresh(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [userId, refresh]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return market;
    return market.filter((c) =>
      `${c.card_name} ${c.card_code} ${c.rarity} ${c.owner_name}`.toLowerCase().includes(needle),
    );
  }, [market, q]);

  const giveCard = useMemo(() => stock.find((s) => s.id === give) ?? null, [stock, give]);
  const tradable = useMemo(() => stock.filter((s) => s.status !== "discarded"), [stock]);
  const pending = offers.filter((o) => o.status === "pending");
  const settled = offers.filter((o) => o.status !== "pending");

  async function submit() {
    if (!want || !give) return;
    setBusy(true);
    setNotice(null);
    try {
      await sendFn({ data: { want_pull_id: want.id, give_pull_id: give, message: message.slice(0, 500) } });
      setNotice(`Offer sent to ${want.owner_name} — you'll see it under "Pending offers" until they answer.`);
      setWant(null);
      setStep("compose");
      setGive("");
      setMessage("");
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not send that offer.");
    } finally {
      setBusy(false);
    }
  }

  async function accept(o: TradeOfferRow) {
    setBusy(true);
    setNotice(null);
    setConfirmAccept(null);
    try {
      await acceptFn({ data: { id: o.id } });
      setNotice(`Trade complete — ${o.give_name ?? "their card"} is now in your collection.`);
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not accept that offer.");
    } finally {
      setBusy(false);
    }
  }

  async function respond(o: TradeOfferRow, status: "declined" | "cancelled") {
    setBusy(true);
    setNotice(null);
    try {
      await respondFn({ data: { id: o.id, status } });
      setNotice(status === "declined" ? "Offer declined." : "Offer cancelled.");
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not update that offer.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-10">

      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld Unofficial Card Game · Trading</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld Card Trading</h1>
        <p className="aeo-answer mt-3 max-w-2xl text-sm text-muted-foreground">
          Trade Palworld cards straight between Pal Stars accounts. Flag a card as trade stock in your binder to list
          it, pick a card another collector listed, offer one of your own in exchange, and the two cards swap accounts
          the moment they accept. Grades and noted defects travel with the card.
        </p>
        <p className="mt-3 text-sm">
          <Link to="/binder" className="text-primary hover:underline">List cards from your binder →</Link>{" "}
          · <Link to="/pack-opening" className="text-primary hover:underline">Open free packs</Link>
        </p>
      </header>

      {alerts.length > 0 && (
        <div aria-live="polite" className="space-y-2">
          {alerts.map((a) => (
            <p key={a.id} className="glass flex items-start gap-2 rounded-xl border border-primary/40 p-3 text-sm">
              <span aria-hidden="true">🔔</span>
              <span className="flex-1">{a.text}</span>
              <button
                type="button"
                aria-label="Dismiss notification"
                onClick={() => setAlerts((prev) => prev.filter((x) => x.id !== a.id))}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </p>
          ))}
        </div>
      )}

      {notice && <p className="glass rounded-xl p-3 text-sm text-primary" role="status">{notice}</p>}

      {!signedIn ? (
        <section className="glass rounded-2xl p-6 text-center">
          <h2 className="text-lg font-bold">Sign in to trade cards</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
            Trading swaps cards between accounts, so both collectors need a free Pal Stars account. Accounts also get
            12 free booster packs a day, 20 Palsino spins and a 108-pocket binder.
          </p>
          {!loading && (
            <Link to="/auth" className="mt-4 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
              Create a free account
            </Link>
          )}
        </section>
      ) : (
        <>
          {pending.length > 0 && (
            <section>
              <h2 className="text-xl font-bold">Pending offers</h2>
              <ul className="mt-3 space-y-3">
                {pending.map((o) => (
                  <li key={o.id} className="glass rounded-xl p-4">
                    <p className="text-sm font-semibold">
                      {o.incoming ? `${o.from_name} wants to trade with you` : `You offered ${o.to_name} a trade`}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <TradeSide
                        label={o.incoming ? "They give you" : "You give"}
                        code={o.give_code}
                        name={o.give_name}
                        rarity={o.give_rarity}
                        image={o.give_image}
                        grade={o.give_grade}
                        gradeLabel={o.give_grade_label}
                      />
                      <span aria-hidden="true" className="text-2xl text-primary">⇄</span>
                      <TradeSide
                        label={o.incoming ? "You give" : "You receive"}
                        code={o.want_code}
                        name={o.want_name}
                        rarity={o.want_rarity}
                        image={o.want_image}
                        grade={o.want_grade}
                        gradeLabel={o.want_grade_label}
                      />
                    </div>
                    {o.message && <p className="mt-3 text-sm text-muted-foreground">“{o.message}”</p>}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {o.incoming ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setConfirmAccept(o)}
                            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
                          >
                            Accept trade…
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void respond(o, "declined")}
                            className="rounded-md border px-4 py-2 text-sm font-semibold hover:bg-card disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void respond(o, "cancelled")}
                          className="rounded-md border px-4 py-2 text-sm font-semibold hover:bg-card disabled:opacity-50"
                        >
                          Cancel offer
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-xl font-bold">Trade board</h2>
              <label className="text-sm">
                <span className="sr-only">Search the trade board</span>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search card, rarity or trainer"
                  className="rounded-lg border bg-background/50 px-3 py-1.5 text-sm outline-none focus:ring-2 ring-primary"
                />
              </label>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {market.length} card{market.length === 1 ? "" : "s"} listed by other collectors. Hover or tap a grade to
              see the sub-grades and the defects marked on the card art.
            </p>
            {filtered.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Nothing listed yet. Flag a spare in your <Link to="/binder" className="text-primary hover:underline">binder</Link> as
                trade stock and it shows up here for everyone.
              </p>
            ) : (
              <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                {filtered.map((c) => (
                  <li key={c.id} className="glass rounded-xl p-2 text-center">
                    <CardImage
                      src={c.image}
                      alt={`${c.card_name} ${c.card_code} ${c.rarity} Palworld card listed for trade`}
                      width={150}
                      height={209}
                      responsive
                      className="w-full rounded-lg"
                    />
                    <p className="mt-1 truncate text-[11px] font-semibold">{c.card_name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      <span style={{ color: RARITY_GLOW[c.rarity] }}>{c.rarity}</span> ·{" "}
                      <GradeBadge card={{ grade: Number(c.grade), grade_label: c.grade_label, image: c.image, card_name: c.card_name }} />
                    </p>
                    <p className="truncate text-[10px] text-muted-foreground">{c.owner_name}</p>
                    <button
                      type="button"
                      onClick={() => { setWant(c); setGive(""); setStep("compose"); }}
                      className="mt-2 w-full rounded-md border px-2 py-1.5 text-[11px] font-semibold hover:bg-card"
                    >
                      Offer a swap
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {settled.length > 0 && (
            <section>
              <h2 className="text-xl font-bold">Trade history</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {settled.map((o) => (
                  <li key={o.id} className="glass flex flex-wrap items-center gap-2 rounded-xl p-3">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${o.status === "accepted" ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {o.status}
                    </span>
                    <span className="text-muted-foreground">
                      {o.give_name ?? "card"} ⇄ {o.want_name ?? "card"} · {o.incoming ? `from ${o.from_name}` : `to ${o.to_name}`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {want && (
        <div role="dialog" aria-modal="true" aria-label={`Offer a swap for ${want.card_name}`} className="fixed inset-0 z-[80] grid place-items-center bg-background/85 p-4">
          <div className="glass max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl p-5 space-y-3">
            <h2 className="text-lg font-bold">
              {step === "compose" ? `Offer a swap to ${want.owner_name}` : "Confirm this trade"}
            </h2>
            {step === "compose" ? (
              <>
                <p className="text-sm text-muted-foreground">
                  You want: {want.card_name} ({want.card_code}, {want.rarity}, {want.grade_label})
                </p>
                <label className="block text-sm">
                  <span className="text-muted-foreground">Card you're giving</span>
                  <select
                    value={give}
                    onChange={(e) => setGive(e.target.value)}
                    className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 text-sm outline-none focus:ring-2 ring-primary"
                  >
                    <option value="">Choose one of your cards…</option>
                    {tradable.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.card_name} · {s.card_code} · {s.rarity} · grade {Number(s.grade).toFixed(1)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-muted-foreground">Message (optional)</span>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Why this is a fair swap…"
                    className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 text-sm outline-none focus:ring-2 ring-primary"
                  />
                </label>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Check both sides before sending. If {want.owner_name} accepts, the cards swap accounts straight away.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <TradeSide
                    label="You give"
                    code={giveCard?.card_code ?? null}
                    name={giveCard?.card_name ?? null}
                    rarity={giveCard?.rarity ?? null}
                    image={giveCard?.image ?? null}
                    grade={giveCard ? Number(giveCard.grade) : null}
                    gradeLabel={giveCard?.grade_label ?? null}
                  />
                  <span aria-hidden="true" className="text-2xl text-primary">⇄</span>
                  <TradeSide
                    label="You receive"
                    code={want.card_code}
                    name={want.card_name}
                    rarity={want.rarity}
                    image={want.image}
                    grade={Number(want.grade)}
                    gradeLabel={want.grade_label}
                  />
                </div>
                {message.trim() && <p className="text-sm text-muted-foreground">“{message.trim()}”</p>}
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => (step === "confirm" ? setStep("compose") : setWant(null))}
                className="rounded-md border px-4 py-2 text-sm font-semibold hover:bg-card"
              >
                {step === "confirm" ? "Back" : "Cancel"}
              </button>
              {step === "compose" ? (
                <button
                  type="button"
                  disabled={!give}
                  onClick={() => setStep("confirm")}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
                >
                  Review offer
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!give || busy}
                  onClick={() => void submit()}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
                >
                  {busy ? "Sending…" : "Confirm & send"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmAccept && (
        <div role="dialog" aria-modal="true" aria-label="Confirm this trade" className="fixed inset-0 z-[80] grid place-items-center bg-background/85 p-4">
          <div className="glass w-full max-w-md rounded-2xl p-5 space-y-3">
            <h2 className="text-lg font-bold">Accept this trade?</h2>
            <p className="text-sm text-muted-foreground">
              The two cards swap accounts immediately and the trade cannot be undone. Grades and noted defects travel
              with each card.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <TradeSide
                label="You give"
                code={confirmAccept.want_code}
                name={confirmAccept.want_name}
                rarity={confirmAccept.want_rarity}
                image={confirmAccept.want_image}
                grade={confirmAccept.want_grade === null ? null : Number(confirmAccept.want_grade)}
                gradeLabel={confirmAccept.want_grade_label}
              />
              <span aria-hidden="true" className="text-2xl text-primary">⇄</span>
              <TradeSide
                label="You receive"
                code={confirmAccept.give_code}
                name={confirmAccept.give_name}
                rarity={confirmAccept.give_rarity}
                image={confirmAccept.give_image}
                grade={confirmAccept.give_grade === null ? null : Number(confirmAccept.give_grade)}
                gradeLabel={confirmAccept.give_grade_label}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmAccept(null)} className="rounded-md border px-4 py-2 text-sm font-semibold hover:bg-card">Keep pending</button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void accept(confirmAccept)}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
              >
                {busy ? "Swapping…" : "Confirm trade"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ClusterLinks hub="/trades" heading="Palworld TCG trading guides" />
      <FaqSection items={FAQ.map((f) => ({ q: f.q, a: f.a }))} />
    </div>
  );
}

function TradeSide({
  label,
  code,
  name,
  rarity,
  image,
  grade,
  gradeLabel,
}: {
  label: string;
  code: string | null;
  name: string | null;
  rarity: string | null;
  image: string | null;
  grade: number | null;
  gradeLabel: string | null;
}) {
  return (
    <div className="flex items-center gap-2">
      {image ? (
        <CardImage src={image} alt={`${name ?? "Palworld card"} ${code ?? ""}`} width={54} height={75} className="rounded" />
      ) : (
        <span className="grid h-[75px] w-[54px] place-items-center rounded bg-card text-[10px] text-muted-foreground">gone</span>
      )}
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-semibold">{name ?? "Card removed"}</p>
        <p className="text-[11px] text-muted-foreground">
          {rarity && <span style={{ color: RARITY_GLOW[rarity] }}>{rarity}</span>}
          {grade !== null && (
            <>
              {" · "}
              <GradeBadge card={{ grade: Number(grade), grade_label: gradeLabel ?? undefined, image: image ?? undefined, card_name: name ?? undefined }} />
            </>
          )}
        </p>
      </div>
    </div>
  );
}
